import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./SelectEnhancer.css";

/*
  Styled pop-up list for EVERY <select> in the admin panel.

  Mounted once in App. When a dropdown is clicked (or opened from the
  keyboard) the browser's plain grey list is replaced by this one. The closed
  dropdown is untouched — each page keeps its own styling — and choosing an
  option sets the real <select>'s value and fires a normal change event, so
  every page's onChange keeps working as before.

  Left alone: touch devices (the phone's picker is easier), multi-selects,
  list boxes (size > 1), disabled dropdowns, and any <select data-native>.
*/

const SEARCH_FROM = 9; // show a search box when a list has this many options
const MAX_HEIGHT = 300;

const isTouch = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches;

const enhanceable = (el) =>
  el && el.tagName === "SELECT" && !el.multiple && !(el.size > 1) && !el.disabled &&
  !el.hasAttribute("data-native") && !isTouch();

// Read the options (with optgroup headings) from the real <select>
const readOptions = (el) => {
  const items = [];
  let lastGroup = null;
  Array.from(el.options).forEach((o, index) => {
    if (o.hidden) return;
    const group = o.parentElement && o.parentElement.tagName === "OPTGROUP" ? o.parentElement : null;
    if (group && group !== lastGroup) items.push({ heading: true, label: group.label, key: `g-${index}` });
    lastGroup = group;
    items.push({
      key: `o-${index}`, index, value: o.value, label: o.text,
      disabled: o.disabled || (group && group.disabled),
    });
  });
  return items;
};

// Set the real <select>'s value so React's onChange fires as usual
const choose = (el, value) => {
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value").set;
  setter.call(el, value);
  el.dispatchEvent(new Event("change", { bubbles: true }));
  el.dispatchEvent(new Event("input", { bubbles: true }));
};

const SelectEnhancer = () => {
  const [open, setOpen] = useState(null); // { el, items }
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1); // index into visible options
  const [pos, setPos] = useState(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const typeahead = useRef({ text: "", at: 0 });

  const close = useCallback((refocus = true) => {
    setOpen((cur) => {
      if (cur && refocus) setTimeout(() => cur.el.focus({ preventScroll: true }), 0);
      return null;
    });
    setQuery("");
  }, []);

  const openFor = useCallback((el) => {
    const items = readOptions(el);
    if (!items.some((i) => !i.heading)) return;
    setQuery("");
    setOpen({ el, items });
    el.focus({ preventScroll: true });
  }, []);

  // Open on mouse press / keyboard, instead of the browser's own list
  useEffect(() => {
    const onMouseDown = (e) => {
      const el = e.target.closest ? e.target.closest("select") : null;
      if (!enhanceable(el) || e.button !== 0) return;
      e.preventDefault();
      if (open && open.el === el) close();
      else openFor(el);
    };
    const onKeyDown = (e) => {
      const el = e.target;
      if (open || !enhanceable(el)) return;
      const opens = e.key === " " || e.key === "Enter" || e.key === "F4" || (e.altKey && (e.key === "ArrowDown" || e.key === "ArrowUp"));
      if (opens) { e.preventDefault(); openFor(el); }
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open, openFor, close]);

  const visible = open
    ? open.items.filter((i) => i.heading || !query.trim() || i.label.toLowerCase().includes(query.trim().toLowerCase()))
        // drop headings with nothing under them after filtering
        .filter((i, n, arr) => !i.heading || (arr[n + 1] && !arr[n + 1].heading))
    : [];
  const choices = visible.filter((i) => !i.heading);

  // start on the selected option
  useEffect(() => {
    if (!open) return;
    const sel = choices.findIndex((c) => c.value === open.el.value);
    setActive(sel >= 0 ? sel : choices.findIndex((c) => !c.disabled));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  // Position under (or above) the dropdown; follow it on scroll/resize
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = open.el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) { close(false); return; }
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const upward = below < Math.min(MAX_HEIGHT, 180) && above > below;
      const width = Math.max(r.width, 190);
      setPos({
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
        width,
        top: upward ? undefined : r.bottom + 6,
        bottom: upward ? window.innerHeight - r.top + 6 : undefined,
        maxHeight: Math.min(MAX_HEIGHT, upward ? above : below),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, close]);

  // Keyboard while the list is open; close when clicking elsewhere
  useEffect(() => {
    if (!open) return;
    const step = (from, dir) => {
      let i = from;
      for (let n = 0; n < choices.length; n++) {
        i = Math.max(0, Math.min(choices.length - 1, i + dir));
        if (!choices[i].disabled) return i;
        if (i === 0 || i === choices.length - 1) break;
      }
      return from;
    };
    const onKey = (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => step(a, 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => step(a, -1)); }
      else if (e.key === "Home") { e.preventDefault(); setActive(step(-1, 1)); }
      else if (e.key === "End") { e.preventDefault(); setActive(step(choices.length, -1)); }
      else if (e.key === "Enter" || (e.key === " " && document.activeElement !== searchRef.current)) {
        e.preventDefault();
        const c = choices[active];
        if (c && !c.disabled) { choose(open.el, c.value); close(); }
      } else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
      else if (e.key === "Tab") close(false);
      else if (e.key.length === 1 && document.activeElement !== searchRef.current) {
        // type a letter to jump, like a normal dropdown
        const t = typeahead.current;
        t.text = Date.now() - t.at < 700 ? t.text + e.key.toLowerCase() : e.key.toLowerCase();
        t.at = Date.now();
        const i = choices.findIndex((c) => !c.disabled && c.label.toLowerCase().startsWith(t.text));
        if (i >= 0) setActive(i);
      }
    };
    const onDown = (e) => {
      if (listRef.current && listRef.current.contains(e.target)) return;
      if (e.target === open.el || (open.el.contains && open.el.contains(e.target))) return;
      close(false);
    };
    const onBlur = () => close(false);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown, true);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("blur", onBlur);
    };
  }, [open, choices, active, close]);

  // keep the highlighted option in view
  useEffect(() => {
    const node = listRef.current && listRef.current.querySelector('[data-active="true"]');
    if (node && node.scrollIntoView) node.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open || !pos) return null;
  const showSearch = open.items.filter((i) => !i.heading).length >= SEARCH_FROM;
  const current = open.el.value;
  let choiceNo = -1;

  return createPortal(
    <div ref={listRef} className={`sx-pop ${pos.bottom !== undefined ? "upward" : ""}`}
      style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}>
      {showSearch && (
        <div className="sx-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <input ref={searchRef} autoFocus placeholder="Search…" value={query} aria-label="Search options"
            onChange={(e) => setQuery(e.target.value)} />
        </div>
      )}
      <ul className="sx-list" role="listbox" aria-label={open.el.getAttribute("aria-label") || open.el.name || "Options"}>
        {visible.map((item) => {
          if (item.heading) return <li key={item.key} className="sx-heading" role="presentation">{item.label}</li>;
          choiceNo += 1;
          const n = choiceNo;
          const selected = item.value === current;
          return (
            <li key={item.key} role="option" aria-selected={selected} aria-disabled={item.disabled || undefined}
              data-active={n === active}
              className={`sx-option ${selected ? "selected" : ""} ${n === active ? "active" : ""} ${item.disabled ? "disabled" : ""}`}
              onMouseEnter={() => !item.disabled && setActive(n)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { if (!item.disabled) { choose(open.el, item.value); close(); } }}>
              <span className="sx-label">{item.label || <em>(blank)</em>}</span>
              {selected && <span className="sx-check" aria-hidden="true">✓</span>}
            </li>
          );
        })}
        {choices.length === 0 && <li className="sx-empty">No match for “{query}”</li>}
      </ul>
    </div>,
    document.body
  );
};

export default SelectEnhancer;
